import { navigate as nav } from 'gatsby';
import { trackButtonClick } from './eventTracking';

// Remove these after the following issue is fixed:
// https://github.com/gatsbyjs/gatsby/issues/39158
interface NavigateOptions<TState> {
  state?: TState;
  replace?: boolean;
}

interface NavigateFn {
  (to: string | number, options?: NavigateOptions<unknown>): Promise<void>;
}

const isExternalLink = (url: string): boolean => {
  const resolved = new URL(url, window.location.href);
  return resolved.origin !== window.location.origin;
};

// Checks that all these conditions are true:
// 1. User has clicked on the link using the mouse's left button without combining it with a modifier key
// 2. Navigation is not suppressed
// 3. event.preventDefault() has not been called
const isGatsbyNavigable = (
  event: React.MouseEvent,
  supressNavigation?: boolean
): boolean =>
  event.button === 0 && // ignore right clicks
  !(event.target as HTMLAnchorElement)?.target && // let browser handle "target=_blank"  // Q4A would this be necessary?
  !supressNavigation && // for ctas the event target is the span rather than the <a> so the above check doesn't work
  !event.defaultPrevented && // onClick prevented default
  !event.metaKey && // ignore clicks with modifier keys...
  !event.altKey &&
  !event.ctrlKey &&
  !event.shiftKey;

export const navigate = (
  url: string,
  options?: {
    state?: unknown;
    replace?: boolean;
  }
): void => {
  (nav as (...args: Parameters<NavigateFn>) => void)(url, options);
};

export const handleLinkClick =
  (url: string, suppressNavigation?: boolean) =>
  (event: React.MouseEvent): void => {
    // Hash and query links aren't supported by Gatsby nor should they be opened in a new tab
    // Let the browser handle these links.
    if (url.startsWith('#') || url.startsWith('?')) return;

    // External links are opened in a new tab
    if (isExternalLink(url)) {
      event.preventDefault();
      const resolved = new URL(url, window.location.href);
      window.open(resolved, '_blank');
      return;
    }

    // Gatsby will handle these internal links - Therefore we may call Gatsby's navigate function
    // See https://github.com/gatsbyjs/gatsby/blob/master/packages/gatsby-link/src/index.js#L227
    if (isGatsbyNavigable(event, suppressNavigation)) {
      event.preventDefault();
      // Restore this after the following issue is fixed:
      // https://github.com/gatsbyjs/gatsby/issues/39158
      navigate(url);
    }
  };

export const handleAndTrackLinkClick =
  (url: string, style: string, label: string, suppressNavigation?: boolean) =>
  (event: React.MouseEvent): void => {
    trackButtonClick(style, label)();
    handleLinkClick(url, suppressNavigation)(event);
  };
